/*
* Main module for SciCharts to be imported by code that needs to use it
* Imports material design modules and angular flex-layout modules
*/
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BREAKPOINT, FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule
} from '@angular/material';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { StorageModule } from '@ngx-pwa/local-storage';
import { MomentModule } from 'ngx-moment';

import { AnnotationsBarComponent } from './components/annotations-buttons/annotations-bar.component';
import { ColorToolsComponent } from './components/annotations-buttons/color-tools.component';
import { DrawingToolsComponent } from './components/annotations-buttons/drawing-tools.component';
import { LineToolsComponent } from './components/annotations-buttons/line-tools.component';
import { SelectToolsComponent } from './components/annotations-buttons/select-tools.component';
import { UndoAnnotationsComponent } from './components/annotations-buttons/undo-annotations.component';
import { UndoZoomComponent } from './components/annotations-buttons/undo-zoom.component';
import { ZoomToolsComponent } from './components/annotations-buttons/zoom-tools.component';
import { CommentBarComponent } from './components/comment-bar/comment-bar.component';
import { CommentDialogComponent } from './components/comment-bar/comment-dialog.component';
import { DatepickerUTCDOYComponent } from './components/datepickers/datepicker-utc-yyyyddd.component';
import { DatepickerUTCComponent } from './components/datepickers/datepicker-utc-yyyymmdd.component';
import { DownloadDialogComponent } from './components/download-dialog/download-dialog.component';
import { DownloadMenuComponent } from './components/download-menu/download-menu.component';
import { EditCommentBubbleDialogComponent } from './components/edit-comment-dialog/edit-comment-dialog.component';
import { FilterMenuComponent } from './components/filter-menu/filter-menu.component';
import { ImageViewerComponent } from './components/image-viewer/image-viewer.component';
import { InfoDialogButtonComponent, InfoDialogComponent } from './components/info-dialog/info-dialog.component';
import { LineChartComponent } from './components/line-chart/line-chart.component';
import { LoadingBarComponent } from './components/loading-bar/loading-bar.component';
import { CheckedIconComponent } from './components/menu-bar/checked-icon.component';
import { MenuBarComponent } from './components/menu-bar/menu-bar.component';
import { MenuComponent } from './components/menu/menu.component';
import { RangeButtonsComponent } from './components/range-buttons/range-buttons.component';
import { RangeSelectorDialogComponent } from './components/range-selector-dialog/range-selector-dialog.component';
import { TextViewerComponent } from './components/text-viewer/text-viewer.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { GridComponent } from './containers/grid.container';
import { SciChartsComponent } from './containers/scicharts.container';
import { StopClickDirective } from './directives/stop-click.directive';

const MATERIAL_MODULES = [
    MatCardModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTabsModule,
    MatTableModule,
    MatRadioModule,
    MatDividerModule,
    MatTooltipModule,
    MatInputModule,
    MatListModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatSelectModule,
    MatGridListModule
];

const COMPONENTS = [
    MenuComponent,
    FilterMenuComponent,
    SciChartsComponent,
    InfoDialogComponent,
    DownloadDialogComponent,
    DownloadMenuComponent,
    StopClickDirective,
    LoadingBarComponent,
    InfoDialogButtonComponent,
    RangeButtonsComponent,
    RangeSelectorDialogComponent,
    DatepickerUTCComponent,
    DatepickerUTCDOYComponent,
    DrawingToolsComponent,
    ZoomToolsComponent,
    SelectToolsComponent,
    ColorToolsComponent,
    LineToolsComponent,
    UndoAnnotationsComponent,
    UndoZoomComponent,
    GridComponent,
    AnnotationsBarComponent,
    MenuBarComponent,
    CheckedIconComponent,
    CommentBarComponent,
    CommentDialogComponent,
    EditCommentBubbleDialogComponent,
    ImageViewerComponent,
    LineChartComponent,
    TextViewerComponent,
    ToolbarComponent
];

const PRINT_BREAKPOINTS = [ {
    alias: 'xs.print',
    suffix: 'XsPrint',
    mediaQuery: 'print and (max-width: 297px)',
    overlapping: false
} ];

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        MATERIAL_MODULES,
        FlexLayoutModule,
        MomentModule,
        StorageModule.forRoot({ IDBNoWrap: true })
    ],
    declarations: COMPONENTS,
    providers: [
        { provide: BREAKPOINT, useValue: PRINT_BREAKPOINTS, multi: true },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [ MAT_DATE_LOCALE ] }
    ],
    entryComponents: [
        InfoDialogComponent,
        DownloadMenuComponent,
        DownloadDialogComponent,
        RangeSelectorDialogComponent,
        CommentDialogComponent,
        EditCommentBubbleDialogComponent
    ],
    exports: COMPONENTS
})
export class ChartModule {}
